import { useMemo, useState } from 'react'
import {
  TIME_BUCKETS, NEAR_TERM_BUCKETS, groupByBucket, makeComparator,
} from '../../../lib/championships.js'
import RecordTable from './RecordTable.jsx'
import styles from './pursuit.module.css'

/**
 * Flat worklist grouped by the rolling 30/60/90 look-back / look-forward
 * buckets, sorted by days_to_event. The period filter is front-and-center and
 * defaults to the near-term windows; every bucket — including the out-of-window
 * Earlier / Later tails — is selectable, and each one pages to its full list
 * rather than truncating.
 */
const PAGE = 200

export default function Worklist({ items, now, onOpen, selection = null, noun = 'Championship', dateLabel = 'Event date', activePeriods, onPeriodsChange }) {
  const raw = useMemo(() => groupByBucket(items, now), [items, now])
  // Pursuit-only column, same rule as everywhere else: rendered where the
  // records carry it, absent from the championship table.
  const hasProduct = useMemo(() => items.some((c) => c.product), [items])
  const [sort, setSort] = useState({ field: 'date', dir: 'asc' })
  const cmp = useMemo(() => makeComparator(sort.field, sort.dir, now), [sort, now])
  // Sorting applies WITHIN each bucket — the timing spine still orders the page.
  const groups = useMemo(() => {
    const g = {}
    for (const b of TIME_BUCKETS) g[b.key] = [...raw[b.key]].sort(cmp)
    return g
  }, [raw, cmp])
  const [localActive, setLocalActive] = useState(() => new Set(NEAR_TERM_BUCKETS))
  const active = activePeriods ?? localActive
  const setActive = onPeriodsChange ?? setLocalActive
  const [limits, setLimits] = useState(() => ({}))
  const limitFor = (k) => limits[k] ?? PAGE
  const bump = (k, n) => setLimits((s) => ({ ...s, [k]: n }))

  const toggle = (key) =>
    setActive((prev) => {
      const n = new Set(prev)
      n.has(key) ? n.delete(key) : n.add(key)
      return n
    })
  const showAll = () => setActive(new Set(TIME_BUCKETS.map((b) => b.key)))
  const sortFields = [['date', dateLabel], ['name', noun], ['owner', 'Assigned rep'], ['sport', 'Sport / level'], ['pursuit', 'Status'], ['state', 'State'], ...(hasProduct ? [['product', 'Product']] : [])]
  const showNear = () => setActive(new Set(NEAR_TERM_BUCKETS))

  return (
    <div className={styles.worklist}>
      {/* Period filter — front and centre */}
      <div className={styles.periodBar}>
        <span className={styles.periodLabel}>Period</span>
        {TIME_BUCKETS.map((b) => (
          <button
            key={b.key}
            className={`${styles.periodPill} ${active.has(b.key) ? styles.periodOn : ''}`}
            aria-pressed={active.has(b.key)}
            onClick={() => toggle(b.key)}
          >
            {b.label}
            <span className={styles.periodCount}>{groups[b.key].length.toLocaleString()}</span>
          </button>
        ))}
        <span className={styles.periodActions}>
          <label className={styles.worklistSort}>Sort <select aria-label="Sort worklist" value={sort.field} onChange={event => setSort(previous => ({...previous,field:event.target.value}))}>
            {sortFields.map(([value,label]) => <option key={value} value={value}>{label}</option>)}
          </select></label>
          <button type="button" className={styles.moreBtn} aria-label="Reverse worklist sort direction" onClick={() => setSort(previous => ({...previous,dir:previous.dir === 'asc' ? 'desc' : 'asc'}))}>{sort.dir === 'asc' ? 'Ascending' : 'Descending'}</button>
          <span className={styles.sortHint}>click a column to sort (incl. Assigned rep)</span>
          <button className={styles.linkBtn} onClick={showNear}>Near-term</button>
          <button className={styles.linkBtn} onClick={showAll}>All</button>
        </span>
      </div>

      {TIME_BUCKETS.filter((b) => active.has(b.key)).map((b) => {
        const list = groups[b.key]
        if (!list.length) return null
        const limit = limitFor(b.key)
        const shown = limit >= list.length ? list : list.slice(0, limit)
        return (
          <section key={b.key} className={styles.bucketSection}>
            <div className={styles.bucketHead} data-tone={b.tone}>
              <span className={styles.bucketName}>{b.label}</span>
              <span className={styles.bucketCount}>{list.length.toLocaleString()}</span>
              {list.length > shown.length && (
                <span className={styles.groupShowing}>showing {shown.length.toLocaleString()} of {list.length.toLocaleString()} in this period</span>
              )}
            </div>

            {list.length > shown.length && <div className={styles.more} data-worklist-paging>
              <span>{shown.length.toLocaleString()} displayed. Select all includes all {list.length.toLocaleString()} in this period.</span>
              <button type="button" className={styles.moreBtn} onClick={() => bump(b.key, limit + PAGE)}>Show next {Math.min(PAGE, list.length - shown.length).toLocaleString()}</button>
              <button type="button" className={styles.moreBtn} onClick={() => bump(b.key, list.length)}>Show all {list.length.toLocaleString()}</button>
            </div>}
            {/* Cards below 640px — see styles/cc-mobile.css. Same DOM, same
                `tName` cell, restyled. Shared with the Calendar's day panel so
                the two lists cannot drift — see RecordTable.jsx. */}
            <div>
              <RecordTable
                rows={shown}
                contactsEnabled
                selectAllRows={list}
                now={now}
                onOpen={onOpen}
                sort={sort}
                setSort={setSort}
                selection={selection}
                noun={noun}
                dateLabel={dateLabel}
                hasProduct={hasProduct}
                selectAllLabel={`rows in ${b.label}`}
              />
              {list.length > shown.length && (
                <div className={styles.more}>
                  <button className={styles.moreBtn} onClick={() => bump(b.key, limit + PAGE * 5)}>
                    Show {Math.min(PAGE * 5, list.length - shown.length).toLocaleString()} more
                  </button>
                  <button className={styles.moreBtn} onClick={() => bump(b.key, list.length)}>
                    Show all {list.length.toLocaleString()}
                  </button>
                </div>
              )}
            </div>
          </section>
        )
      })}
      {[...active].every((k) => groups[k].length === 0) && (
        <div className={styles.emptyState}>No {noun.toLowerCase()} records in the selected periods.</div>
      )}
    </div>
  )
}
